/* eslint-disable camelcase */
import { useState, useEffect, FC } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import { Grid, Typography, Box, Button, CircularProgress, TextField, Accordion, AccordionSummary, AccordionDetails } from '../../../../UI'
import useStyles from './styles'
import { concatSeparator, formatTimestamp, history, isEmpty, ORDER_ITEM_STATUS, ORDER_STATUS, ORDER_TYPE, round } from '../../../../../helpers'
import { getOrder, getOrderItems, getOrderState, allItemsFinished, isAdmin, getPDFUrl } from '../../../../../ducks/selectors'
import { icons } from '../../../../../assets'
import { filesActions, ordersActions } from '../../../../../ducks/actions'

const OrderProfile: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { order: id } = history.getParamValues()
  const order = useSelector(getOrder(id))
  const pdfUrl = useSelector(getPDFUrl)

  const orderState = useSelector(getOrderState(id))
  const allFinished = useSelector(allItemsFinished(id))

  const items = useSelector(getOrderItems(id))
  const itemsFinished = useSelector(getOrderItems(id, [ORDER_ITEM_STATUS.COMPLETE, ORDER_ITEM_STATUS.ACCEPTED, ORDER_ITEM_STATUS.WILL_NOT_ADDRESS]))
  const isOrgAdmin = useSelector(isAdmin)


  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(false)

  const [scheduledStart, setScheduledStart] = useState('')
  const [scheduledEnd, setScheduledEnd] = useState('')

  const totalCount = items?.length ?? 0
  const finishedCount = itemsFinished?.length ?? 0

  useEffect(() => {
    const parse = (ts: number | null | undefined) => {
      if (!ts) return ''
      return dayjs.unix(ts).format('YYYY-MM-DDTHH:mm')
    }
    setScheduledStart(parse(order?.scheduledStartTimestamp))
    setScheduledEnd(parse(order?.scheduledEndTimestamp))
  }, [order?.scheduledStartTimestamp, order?.scheduledEndTimestamp])

  if (!order) return <div>No order</div>
  else {
    const {
      bidPrice, orderName, estimatePublicId, type, statusType,
      checkedInTimestamp, completedTimestamp,
      address: orderAddress,
      assignedByName,
      showPriceToVendor,
      homeownersName, homeownersPhone, homeownersEmail,
      agentsName, agentsPhone, agentsEmail,
      schedulersName, schedulersPhone, schedulersEmail,
      accessInfo, lockboxDetails, closingDate,
      occupancy, childrenUnder18, pets,
      vendorAcceptedTimestamp, preferredStartDate, preferredCompletedOn
    } = order

    const { city = '', zipCode = '', line1 = '', line2 = '' } = orderAddress ?? {}

    const formatBool = (val: boolean | null) => val === null ? 'N/A' : val ? 'Yes' : 'No'

    const showBidPrice = (isOrgAdmin || isEmpty(assignedByName)) && bidPrice !== null && showPriceToVendor

    let address = city
    address = concatSeparator(address, zipCode, ', ')
    address = concatSeparator(address, line2, ', ')

    return (
      <Grid container style={{ display: 'grid' }}>
        <Grid container item style={{ alignItems: 'baseline' }}>
          <Grid container item xs={6} style={{ display: 'grid' }}>
            <Grid item>
              <Typography variant='subtitle1'>
                {line1}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='caption'>
                {address}
              </Typography>
            </Grid>
            {(type === ORDER_TYPE.ORDER && estimatePublicId) && (
              <Grid item>
                <Typography variant='caption'>
                  <b>Job ID:</b> {estimatePublicId}
                </Typography>
              </Grid>)}
            {vendorAcceptedTimestamp && (
              <Grid item>
                <Typography variant='caption'>
                  <b>Job Accepted Date:</b> {formatTimestamp(vendorAcceptedTimestamp, 'MM/DD/YYYY HH:mm')}
                </Typography>
              </Grid>)}
            <Grid item>
              <Typography variant='caption'>
                <b>Preferred Start Date:</b> {preferredStartDate ? formatTimestamp(preferredStartDate, 'MM/DD/YYYY') : 'Flexible'}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant='caption'>
                <b>Preferred Completion Date:</b> {preferredCompletedOn ? formatTimestamp(preferredCompletedOn, 'MM/DD/YYYY') : 'Flexible'}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container item xs={6} style={{
              textAlign: 'end'
            }}
          >
            {showBidPrice && <Grid item xs={12}>
              <Typography variant='h6' color='primary'>
                $ {round(bidPrice, 2)}
              </Typography>
            </Grid>}
            <Grid item xs={12}>
              <Typography variant='caption'>
                {`${orderName || ''}`}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'>
                <b>Items:</b> {`${finishedCount}/${totalCount}`}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid container item alignItems='center' style={{ marginTop: '8px' }}>
          <Grid item xs={4}>
            <TextField
              label='Scheduled Start'
              type='datetime-local'
              fullWidth
              size='small'
              value={scheduledStart}
              onChange={(e) => {
                setScheduledStart(e.target.value)
                if (scheduledEnd && e.target.value >= scheduledEnd) {
                  setScheduledEnd('')
                }
              }}
              disabled={statusType !== ORDER_STATUS.DISPATCHED}
              inputProps={{ min: dayjs().format('YYYY-MM-DDTHH:mm') }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'end' }}>
            <Button
              variant='contained'
              color='primary'
              className={classes.downloadPdf}
              style={{ width: '130px' }}
              disabled={statusType !== ORDER_STATUS.DISPATCHED || !scheduledStart || !scheduledEnd}
              onClick={() => {
                dispatch(ordersActions.updateJobSchedule({
                  orderId: id,
                  schedulingStartDate: dayjs(scheduledStart).unix(),
                  schedulingEndDate: dayjs(scheduledEnd).unix(),
                  expectedCompletedOn: dayjs(scheduledEnd).unix()
                }))
                dispatch(ordersActions.updateOrderValues({
                  orderId: id,
                  values: {
                    scheduledStartTimestamp: dayjs(scheduledStart).unix(),
                    scheduledEndTimestamp: dayjs(scheduledEnd).unix()
                  },
                }))
              }}
            >
              Save Dates
            </Button>
          </Grid>
          <Grid item xs={4} style={{ marginTop: '8px' }}>
            <TextField
              label='Scheduled Completion Date'
              type='datetime-local'
              fullWidth
              size='small'
              value={scheduledEnd}
              onChange={(e) => setScheduledEnd(e.target.value)}
              disabled={statusType !== ORDER_STATUS.DISPATCHED || !scheduledStart}
              inputProps={{ min: scheduledStart || dayjs().format('YYYY-MM-DDTHH:mm') }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'end', marginTop: '8px' }}>
            {totalCount > 0 && (
              <Button
                variant='outlined'
                color='primary'
                className={classes.downloadPdf}
                style={{ width: '130px' }}
                startIcon={downloadingPdf
                  ? <CircularProgress color='inherit' size={20} />
                  : <icons.GetApp />}
                onClick={() => {
                  if (!downloadingPdf) {
                    setDownloadingPdf(true)
                    dispatch(
                      filesActions.downloadFile({
                        documentType: 'WORK_ORDER_PRO_APP',
                        resourceId: id,
                        parameters: {
                          hidePricing: !showBidPrice
                        }
                      }, (succ: boolean) => {
                        setDownloadingPdf(false)
                        if (succ && pdfUrl) {
                          window.open(pdfUrl, '_blank')
                        }
                      })
                    )
                  }
                }}
              >
                PDF
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid container item className={classes.infoBox}>
          <Accordion
            expanded={detailsExpanded}
            onChange={(_, isExpanded) => setDetailsExpanded(isExpanded)}
            elevation={0}
            disableGutters
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<icons.ExpandMore />}>
              <Typography variant='caption'><b>Job Details</b></Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
          <Grid container item className={classes.info}>
            <Grid container item xs={7} sm={6} className={classes.infoContent}>
              {(orderState === 'checkin' || orderState === 'complete') && (
                <Grid item xs={12}>
                  <Typography variant='caption'><b>Checkin Date: </b></Typography>
                  <Typography variant='caption'>
                    {formatTimestamp(checkedInTimestamp, 'MM/DD/YYYY HH:mm')}
                  </Typography>
                </Grid>)}
              {orderState === 'complete' && (
                <Grid item xs={12}>
                  <Typography variant='caption'><b>Completed Date: </b></Typography>
                  <Typography variant='caption'>
                    {formatTimestamp(completedTimestamp, 'MM/DD/YYYY HH:mm')}
                  </Typography>
                </Grid>)}
            </Grid>
            <Grid container item xs={5} sm={6} className={classes.statusContainer}>
              {(orderState === 'complete' && allFinished) && (
                <Box className={`${classes.status} ${classes.backgroundComplete}`}>
                  <Typography variant='caption' className={classes.statusComplete}><b>COMPLETED</b></Typography>
                  <icons.CheckCircle className={`${classes.statusIcon} ${classes.statusComplete}`} />
                </Box>
              )}
              {orderState === 'checkin' && !allFinished && (
                <Box className={`${classes.status} ${classes.backgroundProgress}`}>
                  <Typography variant='caption' className={classes.statusProgress}><b>IN PROGRESS</b></Typography>
                  <icons.CheckCircle className={`${classes.statusIcon} ${classes.statusProgress}`} />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Homeowner Name: </b></Typography>
              <Typography variant='caption'>{homeownersName ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Homeowner Phone: </b></Typography>
              <Typography variant='caption'>{homeownersPhone ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Homeowner Email: </b></Typography>
              <Typography variant='caption'>{homeownersEmail ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '8px' }}>
              <Typography variant='caption'><b>Agent Name: </b></Typography>
              <Typography variant='caption'>{agentsName ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Agent Phone: </b></Typography>
              <Typography variant='caption'>{agentsPhone ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Agent Email: </b></Typography>
              <Typography variant='caption'>{agentsEmail ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '8px' }}>
              <Typography variant='caption'><b>Scheduler Name: </b></Typography>
              <Typography variant='caption'>{schedulersName ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Scheduler Phone: </b></Typography>
              <Typography variant='caption'>{schedulersPhone ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Scheduler Email: </b></Typography>
              <Typography variant='caption'>{schedulersEmail ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '8px' }}>
              <Typography variant='caption'><b>Access Info: </b></Typography>
              <Typography variant='caption'>{accessInfo ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Lockbox Details: </b></Typography>
              <Typography variant='caption'>{lockboxDetails ?? 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Closing Date: </b></Typography>
              <Typography variant='caption'>{closingDate ? formatTimestamp(closingDate, 'MM/DD/YYYY') : 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Occupancy: </b></Typography>
              <Typography variant='caption'>{formatBool(occupancy)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Children Under 18: </b></Typography>
              <Typography variant='caption'>{formatBool(childrenUnder18)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant='caption'><b>Pets: </b></Typography>
              <Typography variant='caption'>{formatBool(pets)}</Typography>
            </Grid>
          </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    )
  }
}

export default OrderProfile
